/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import { AddressField } from "cms/fields/fields/field-address";
import { BooleanField } from "cms/fields/fields/field-boolean";
import { ComponentField } from "cms/fields/fields/field-component";
import { CurrencyField } from "cms/fields/fields/field-currency";
import { DateField } from "cms/fields/fields/field-date";
import { DateTimeField } from "cms/fields/fields/field-date-time";
import { DecimalField } from "cms/fields/fields/field-decimal";
import { DocumentField } from "cms/fields/fields/field-document";
import { EmailField } from "cms/fields/fields/field-email";
import { EmbedField } from "cms/fields/fields/field-embed";
import { EmbedBuilderField } from "cms/fields/fields/field-embed-builder";
import { EnumField } from "cms/fields/fields/field-enum";
import { HeadingField } from "cms/fields/fields/field-heading";
import { ImageField } from "cms/fields/fields/field-image";
import { JSONField } from "cms/fields/fields/field-json";
import { LinkField } from "cms/fields/fields/field-link";
import { ModelRefField } from "cms/fields/fields/field-model-ref";
import { MultiLineField } from "cms/fields/fields/field-multi-line-text";
import { NumberField } from "cms/fields/fields/field-number";
import { PhoneNumberField } from "cms/fields/fields/field-phone-number";
import { RichTextField } from "cms/fields/fields/field-richtext";
import { TextField } from "cms/fields/fields/field-text";
import { TimeField } from "cms/fields/fields/field-time";
import { TimezoneField } from "cms/fields/fields/field-timezone";
import { UrlField } from "cms/fields/fields/field-url";
import { WebpageRefField } from "cms/fields/fields/field-webpage-ref";
import { NotImplementedField } from "cms/fields/fields/field-not_implemented";

const DEFAULT_STYLE =  "field.style.body";
const DEFAULT_FORMAT =  "field.format.none";
const DEFAULT_SETTINGS = { style: DEFAULT_STYLE, format: DEFAULT_FORMAT, options: {} };

export const fieldTypes = [
    {
        type: "field.address",
        text: "Address",
        component: AddressField,
        defaultImpl: {
            value: "",
            settings: DEFAULT_SETTINGS
        },
    },
    {
        type: "field.boolean",
        text: "Boolean",
        component: BooleanField,
        defaultImpl: {
            value: false,
            settings: { style: DEFAULT_STYLE, format: DEFAULT_FORMAT, options: { labelText: "Online" } }
        },
    },
    {
        type: "field.component",
        text: "Component",
        component: ComponentField,
        defaultImpl: { settings: DEFAULT_SETTINGS, value: [] },
    },
    {
        type: "field.currency",
        text: "Currency",
        component: CurrencyField,
        defaultImpl: { settings: DEFAULT_SETTINGS, value: 0.00 },
    },
    {
        type: "field.date",
        text: "Date",
        component: DateField,
        defaultImpl: { settings: DEFAULT_SETTINGS, value: "" },
    },
    {
        type: "field.datetime",
        text: "Datetime",
        component: DateTimeField,
        defaultImpl: { settings: DEFAULT_SETTINGS, value: "" },
    },
    {
        type: "field.decimal",
        text: "Decimal",
        // subTitle: 'Decimal',
        component: DecimalField,
        defaultImpl: { settings: DEFAULT_SETTINGS, value: 0 },
    },
    {
        type: "field.document",
        text: "Document",
        component: DocumentField,
        defaultImpl: {
            value: "",
            settings: {
                style: DEFAULT_STYLE,
                format: "field.format.document-title-size-icon",
                options: { openIn: "field.target.tab" } }
        },
    },
    {
        type: "field.email",
        text: "Email",
        component: EmailField,
        defaultImpl: { settings: DEFAULT_SETTINGS, value: "" },
    },
    {
        type: "field.embed",
        text: "Embed",
        component: EmbedField,
        defaultImpl: { settings: DEFAULT_SETTINGS, value: "" },
    },
    {
        type: "field.embed-builder",
        text: "Embed Builder",
        component: EmbedBuilderField,
        defaultImpl: { settings: DEFAULT_SETTINGS, value: "" },
    },
    {
        type: "field.enum",
        text: "Selection",
        component: EnumField,
        defaultImpl: { settings: DEFAULT_SETTINGS, value: "" },
    },
    {
        type: "field.file",
        text: "File",
        component: NotImplementedField,
        defaultImpl: { settings: DEFAULT_SETTINGS, value: "" },
    },
    {
        type: "field.heading",
        text: "Heading Text",
        component: HeadingField,
        defaultImpl: { settings: DEFAULT_SETTINGS, value: "" },
    },
    {
        type: "field.image",
        text: "Image",
        component: ImageField,
        defaultImpl: {
            value: "",
            settings: {
                style: "field.style.none",
                format: "field.format.cover",
                options: { position: "center center", target: "new" },
            },
        },
    },
    {
        type: "field.json",
        text: "JSON",
        component: JSONField,
        defaultImpl: { settings: DEFAULT_SETTINGS, value: "" },
    },
    {
        type: "field.link",
        text: "Link",
        component: LinkField,
        defaultImpl: {
            value: "",
            settings: {
                style: "field.style.body",
                format: "field.link.text-only",              // Notice link vs format here
                options: { openIn: "field.target.tab" }
            }
        },
    },
    {
        type: "field.location",
        text: "Location",
        // component: SIMFieldLocation,
        component: NotImplementedField,
        defaultImpl: { settings: DEFAULT_SETTINGS, value: "" },
    },
    {
        type: "field.map",
        text: "Map",
        component: NotImplementedField,
        defaultImpl: { settings: DEFAULT_SETTINGS, value: "" },
    },
    {
        type: "field.model-ref",
        text: "Model Reference",
        component: ModelRefField,
        defaultImpl: { settings: DEFAULT_SETTINGS, value: "" },
    },
    {
        type: "field.multi-line-text",
        text: "Multi-line TextField",
        component: MultiLineField,
        defaultImpl: { settings: DEFAULT_SETTINGS, value: "" },
    },
    {
        type: "field.number",
        text: "Number",
        component: NumberField,
        defaultImpl: { settings: DEFAULT_SETTINGS, value: 0 },
    },
    {
        type: "field.phone-number",
        text: "Phone",
        component: PhoneNumberField,
        defaultImpl: { settings: DEFAULT_SETTINGS, value: "" },
    },
    {
        type: "field.richtext",
        text: "Rich Text",
        component: RichTextField,
        defaultImpl: { settings: DEFAULT_SETTINGS, value: "" },
    },
    {
        type: "field.text",
        text: "Text",
        component: TextField,
        defaultImpl: { settings: DEFAULT_SETTINGS, value: "" },
    },
    {
        type: "field.time",
        text: "Time",
        component: TimeField,
        defaultImpl: { settings: DEFAULT_SETTINGS, value: "" },
    },
    {
        type: "field.timezone",
        text: "Timezone",
        component: TimezoneField,
        defaultImpl: { settings: DEFAULT_SETTINGS, value: "" },
    },
    {
        type: "field.url",
        text: "Url",
        component: UrlField,
        defaultImpl: { settings: DEFAULT_SETTINGS, value: "" },
    },
    {
        type: "field.webpage-ref",
        text: "Webpage Reference",
        component: WebpageRefField,
        defaultImpl: { settings: DEFAULT_SETTINGS, value: "" },
    },
    {
        type: "field.video",
        text: "Video",
        component: NotImplementedField,
        defaultImpl: { settings: DEFAULT_SETTINGS, value: "" },
    },
    {
        type: "MAGIC",
        text: "Magic",
        disabled: true,
        component: null,
        defaultImpl: {},
    },
];
